"use client"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { convertToLocale } from "@/lib/util/money"
import { PriceChangeNotice, PriceValidation } from "@/types/store"
import { Prompt } from "@medusajs/ui"
import PlaceholderImage from "@modules/common/icons/placeholder-image"
import Thumbnail from "@modules/products/components/thumbnail"
import { AlertTriangle, TrendingDown, TrendingUp } from "lucide-react"
import { useTranslation } from "react-i18next"

interface PriceChangeModalProps {
    isOpen: boolean
    onClose: () => void
    priceValidation: PriceValidation
}

export function PriceChangeModal({
    isOpen,
    onClose,
    priceValidation,
}: PriceChangeModalProps) {
    const { t } = useTranslation()
    
    // If modal is not open, don't render
    if (!isOpen) {
        return null
    }

    // Once the modal is open, only check if we have valid priceValidation data
    // Don't check hasPriceChanges because it might be cleared after cart updates
    // The preserved priceValidation ensures we always have valid data when modal is shown
    if (!priceValidation?.notices?.length) {
        return null
    }

    const notices = priceValidation.notices
    const increasedItems = notices.filter((notice: PriceChangeNotice) => notice.changeDirection === "increased")
    const decreasedItems = notices.filter((notice: PriceChangeNotice) => notice.changeDirection === "decreased")

    return (
        <Prompt open={isOpen} onOpenChange={onClose}>
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
            <Prompt.Content
                className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-bg-component p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
            >
                <div className="rounded-lg bg-muted-accent/80 backdrop-blur-sm p-4 border border-muted-accent">
                    <div className="flex items-center justify-center space-x-2 text-foreground">
                        <AlertTriangle className="h-5 w-5 text-button-primary" />
                        <span className="font-medium">{t("cart.priceChangesDetected")}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground text-center">
                        {t("cart.priceChangesDescription")}
                    </p>
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                    {increasedItems.length > 0 && (
                        <div>
                            <h3 className="flex items-center gap-2 mb-3 text-sm font-semibold text-destructive">
                                <TrendingUp className="w-4 h-4" />
                                {t("cart.priceIncreases")} ({increasedItems.length})
                            </h3>
                            <div className="space-y-2">
                                {increasedItems.map((notice: PriceChangeNotice, index: number) => {
                                    // Try to get product image from the cart data
                                    let thumbnailUrl = null
                                    // For now, we'll use placeholder since we don't have direct access to product images
                                    // In a real implementation, you'd fetch the product data to get the image

                                    return (
                                        <Card key={index} className="p-4 bg-bg-component border-muted-accent">
                                            <div className="flex flex-col gap-4 pb-6 sm:flex-row">
                                                <div className="flex-shrink-0">
                                                    <div className="overflow-hidden relative w-20 h-20 rounded-lg">
                                                        {thumbnailUrl ? (
                                                            <Thumbnail
                                                                thumbnail={thumbnailUrl}
                                                                images={[]}
                                                                size="square"
                                                            />
                                                        ) : (
                                                            <PlaceholderImage className="w-full h-full" />
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex-grow">
                                                    <div className="flex flex-col sm:flex-row sm:justify-between">
                                                        <div>
                                                            <div className="flex gap-2 items-center">
                                                                <h3 className="font-medium text-foreground">
                                                                    {notice.productName}
                                                                </h3>
                                                            </div>
                                                            <p className="mt-1 text-sm text-muted-foreground">
                                                                {t("product.seller")}: 101 YAPI
                                                            </p>
                                                        </div>

                                                        <div className="mt-2 sm:mt-0 text-right max-w-[200px]">
                                                            <div className="flex flex-col items-end gap-1">
                                                                <span className="text-sm text-muted-foreground line-through">
                                                                    {convertToLocale({
                                                                        amount: notice.oldPrice,
                                                                        currency_code: "TRY",
                                                                    })}
                                                                </span>
                                                                <span className="text-sm text-destructive font-semibold">
                                                                    {convertToLocale({
                                                                        amount: notice.newPrice,
                                                                        currency_code: "TRY",
                                                                    })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {decreasedItems.length > 0 && (
                        <div>
                            <h3 className="flex items-center gap-2 mb-3 text-sm font-semibold text-button-primary">
                                <TrendingDown className="w-4 h-4" />
                                {t("cart.priceDecreases")} ({decreasedItems.length})
                            </h3>
                            <div className="space-y-2">
                                {decreasedItems.map((notice: PriceChangeNotice, index: number) => {
                                    // Try to get product image from the cart data
                                    let thumbnailUrl = null
                                    // For now, we'll use placeholder since we don't have direct access to product images
                                    // In a real implementation, you'd fetch the product data to get the image

                                    return (
                                        <Card key={index} className="p-4 bg-bg-component border-muted-accent">
                                            <div className="flex flex-col gap-4 pb-6 border-b border-muted-accent sm:flex-row">
                                                <div className="flex-shrink-0">
                                                    <div className="overflow-hidden relative w-20 h-20 rounded-lg">
                                                        {thumbnailUrl ? (
                                                            <Thumbnail
                                                                thumbnail={thumbnailUrl}
                                                                images={[]}
                                                                size="square"
                                                            />
                                                        ) : (
                                                            <PlaceholderImage className="w-full h-full" />
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex-grow">
                                                    <div className="flex flex-col sm:flex-row sm:justify-between">
                                                        <div>
                                                            <div className="flex gap-2 items-center">
                                                                <h3 className="font-medium text-foreground">
                                                                    {notice.productName}
                                                                </h3>
                                                            </div>
                                                            <p className="mt-1 text-sm text-muted-foreground">
                                                                {t("product.seller")}: 101 YAPI
                                                            </p>
                                                        </div>

                                                        <div className="mt-2 sm:mt-0 text-right max-w-[200px]">
                                                            <div className="flex flex-col items-end gap-1">
                                                                <span className="text-sm text-muted-foreground line-through">
                                                                    {convertToLocale({
                                                                        amount: notice.oldPrice,
                                                                        currency_code: "TRY",
                                                                    })}
                                                                </span>
                                                                <span className="text-sm text-button-primary font-semibold">
                                                                    {convertToLocale({
                                                                        amount: notice.newPrice,
                                                                        currency_code: "TRY",
                                                                    })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-4 text-center">
                    <Button
                        onClick={onClose}
                        className="w-full text-sm bg-button-primary hover:bg-button-primary-lighten text-slate-900 px-3 rounded-lg"
                    >
                        {t("cart.continueWithUpdatedPrices")}
                    </Button>
                </div>
            </Prompt.Content>
        </Prompt>
    )
}
